import React, { useEffect, useMemo, useRef } from 'react';
import css from './PreviewPane.module.scss';

interface PreviewPaneProps {
  /**
   * Full preview document from GET /versions/{id}/preview. Every change
   * navigates the iframe — a full document reload that resets scroll
   * position. Pages must therefore pass `loading=true` only for the INITIAL
   * fetch and keep the stale doc visible (loading=false) during refetches,
   * or the pane flashes a skeleton on every theme/comment round-trip.
   */
  html: string;
  /**
   * Section to glow once the freshly loaded preview settles (accept flow).
   * Fires only on the NEXT document load and is consumed after posting — a
   * later reload (e.g. a theme-change refetch) never re-fires a stale
   * highlight or scroll-yanks. Pass a new value to arm it again.
   */
  highlightSectionId?: string | null;
  /**
   * Glow a section on the CURRENTLY loaded document without a reload — used
   * when a preview click spawns a card, to flash the very element the user
   * tapped (the mirror of the accept-flow glow). The `nonce` re-fires the glow
   * even when the same section is pulsed twice; a bare section id would not
   * change and the effect would not re-run.
   */
  pulse?: { sectionId: string; nonce: number } | null;
  onSectionClick: (sectionId: string) => void;
  /** Click on a preview area with no comment target (the contact header) —
   *  the template posts a distinct message so the page can explain where
   *  comments work instead of ignoring the tap. */
  onUnwiredClick?: () => void;
  /**
   * Read-only mode. Defaults to `true` (the editor's interactive preview). When
   * `false` the document renders WITHOUT the hover/cursor/click/glow snippet —
   * used by the /searches card drawer, where the resume is a document to read,
   * not an editor to click into.
   */
  interactive?: boolean;
  loading: boolean;
}

/* Appended client-side before </body> — the backend HTML already ships the
 * click → postMessage script; we ADD (1) a hover affordance so users discover
 * that sections are clickable, and (2) the change-glow listener. All of it is
 * screen-only (print output must never carry cursor/hover/highlight) and the
 * animated forms are gated on prefers-reduced-motion; reduced-motion users
 * still get a static highlight + a non-smooth scroll so they can see WHERE a
 * change landed. `.beast-hover` / `.beast-glow` are toggled on the SAME element
 * `closest('[data-section-id]')` resolves to — i.e. exactly the click target —
 * so hovering a bullet lights the bullet, not its whole section. */
const INTERACTION_SNIPPET = `
<style>
[data-section-id] { cursor: pointer; }
@media screen and (prefers-reduced-motion: no-preference) {
  [data-section-id] { transition: background 0.12s ease; }
  .beast-hover { background: rgba(32, 117, 81, 0.06); border-radius: 6px; }
  /* A single self-contained pulse: strong green ring + tint, a brief hold, then
     a clean fade to nothing. Driven by ONE class add (no second class-toggle),
     so it can never race itself into invisibility. */
  @keyframes beastGlowPulse {
    0%   { background: rgba(32, 117, 81, 0.28); box-shadow: 0 0 0 3px rgba(32, 117, 81, 0.55); }
    22%  { background: rgba(32, 117, 81, 0.28); box-shadow: 0 0 0 3px rgba(32, 117, 81, 0.55); }
    100% { background: transparent; box-shadow: 0 0 0 3px rgba(32, 117, 81, 0); }
  }
  .beast-glow { border-radius: 6px; animation: beastGlowPulse 2s ease-out; }
}
@media screen and (prefers-reduced-motion: reduce) {
  .beast-hover { background: rgba(32, 117, 81, 0.06); border-radius: 6px; }
  .beast-glow { background: rgba(32, 117, 81, 0.16); border-radius: 6px; }
}
</style>
<script>
(function () {
  var hovered = null;
  function setHover(el) {
    if (el === hovered) return;
    if (hovered) hovered.classList.remove('beast-hover');
    hovered = el;
    if (el) el.classList.add('beast-hover');
  }
  document.addEventListener('mouseover', function (ev) {
    setHover(ev.target.closest('[data-section-id]'));
  });
  document.addEventListener('mouseout', function (ev) {
    if (!ev.relatedTarget) setHover(null);
  });
  window.addEventListener('message', function (ev) {
    if (!ev.data || ev.data.type !== 'beast-resume-highlight') return;
    var el = document.querySelector('[data-section-id="' + ev.data.sectionId + '"]');
    if (!el) return;
    // Restart the animation even when the SAME element is glowed twice in a row
    // (e.g. two comments on one section): drop the class, force a reflow, re-add.
    el.classList.remove('beast-glow');
    void el.offsetWidth;
    el.classList.add('beast-glow');
    setTimeout(function () { el.classList.remove('beast-glow'); }, 2100);
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
  });
})();
</script>`;

const decorateHtml = (html: string): string =>
  html.includes('</body>')
    ? html.replace('</body>', `${INTERACTION_SNIPPET}</body>`)
    : html + INTERACTION_SNIPPET;

/**
 * Live resume preview: a sandboxed iframe rendering the backend's preview
 * document, decorated with a change-glow listener. Purely presentational —
 * the page fetches the html, decides what to highlight, and reacts to
 * section clicks (comment popover).
 */
const PreviewPane: React.FC<PreviewPaneProps> = ({
  html,
  highlightSectionId = null,
  pulse = null,
  onSectionClick,
  onUnwiredClick,
  interactive = true,
  loading,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Refs keep the message/load handlers stable across renders while always
  // seeing the latest props.
  //
  // The highlight ref is (re)armed only when the PROP changes — not on every
  // render — because handleLoad consumes it after posting. A per-render sync
  // would re-arm the stale value on the next re-render (e.g. the html
  // refetch that follows a theme change) and re-fire the glow/scroll.
  const highlightRef = useRef<string | null>(highlightSectionId ?? null);
  useEffect(() => {
    highlightRef.current = highlightSectionId ?? null;
  }, [highlightSectionId]);
  const onSectionClickRef = useRef(onSectionClick);
  onSectionClickRef.current = onSectionClick;
  const onUnwiredClickRef = useRef(onUnwiredClick);
  onUnwiredClickRef.current = onUnwiredClick;

  // Glow the tapped element on the LIVE document (no reload). Keyed on the
  // pulse object identity — the parent mints a new one (with an incrementing
  // nonce) per click, so glowing the same section twice still re-fires.
  useEffect(() => {
    if (!pulse) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'beast-resume-highlight', sectionId: pulse.sectionId },
      '*'
    );
  }, [pulse]);

  // Read-only mode renders the raw backend document; only the editor's
  // interactive preview gets the hover/click/glow snippet.
  const decoratedHtml = useMemo(
    () => (interactive ? decorateHtml(html) : html),
    [html, interactive]
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const frameWindow = iframeRef.current?.contentWindow ?? null;
      // The backend template posts with targetOrigin '*', so anyone can spoof
      // the payload — only trust OUR iframe's window. The explicit null check
      // matters: when the frame window is unavailable, a null event.source
      // must not slip through as null === null.
      if (!frameWindow || event.source !== frameWindow) return;
      const data = event.data as
        | { type?: unknown; sectionId?: unknown }
        | null;
      if (!data) return;
      if (data.type === 'beast-resume-unwired-click') {
        onUnwiredClickRef.current?.();
        return;
      }
      if (data.type !== 'beast-resume-section-click') return;
      if (typeof data.sectionId !== 'string') return;
      onSectionClickRef.current(data.sectionId);
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // srcDoc changes reload the document, wiping any glow state — so the
  // pending highlight is requested only once the new document is live, and
  // CONSUMED after posting so a later reload never re-fires a stale one.
  const handleLoad = () => {
    const sectionId = highlightRef.current;
    if (!sectionId) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'beast-resume-highlight', sectionId },
      '*'
    );
    highlightRef.current = null;
  };

  if (loading) {
    return (
      <div className={css.pane}>
        <div
          className={css.skeleton}
          role="status"
          aria-label="Loading resume preview"
        />
      </div>
    );
  }

  return (
    <div className={css.pane}>
      <iframe
        ref={iframeRef}
        className={css.frame}
        sandbox="allow-scripts"
        srcDoc={decoratedHtml}
        title="Resume preview"
        onLoad={handleLoad}
      />
    </div>
  );
};

export default PreviewPane;
